import { Venue } from 'types/model'
import { FacebookLogo } from '@icons/facebook'
import { InstagramLogo } from '@icons/instagram'
import { TikTokLogo } from '@icons/tiktok'
import { TwitterLogo } from '@icons/twitter'
import { Link } from '@elements/link'
import { GlobeAltIcon } from '@heroicons/react/24/outline'
import { validateSocialMediaUrl, validateUrl } from '@utils/urls'

type VenueSocialCardType =
  | 'website'
  | 'twitter'
  | 'instagram'
  | 'facebook'
  | 'tiktok'

const socialCardLabels: Record<VenueSocialCardType, string> = {
  website: 'Website',
  twitter: 'Twitter',
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
}

export function VenueSocialCard({
  venue,
  type,
}: {
  venue: Venue
  type: VenueSocialCardType
}) {
  if (type !== 'website' && !venue.details?.social?.[type]) return null
  if (type === 'website' && !venue.website) return null

  // Validate and clean the URL
  const rawUrl =
    type === 'website'
      ? venue.website
      : (venue.details.social?.[type] as string)
  const validatedUrl =
    type === 'website'
      ? validateUrl(rawUrl)
      : validateSocialMediaUrl(
          rawUrl,
          type as 'twitter' | 'facebook' | 'instagram' | 'tiktok'
        )

  // Don't render if URL is invalid
  if (!validatedUrl) return null

  return (
    <Link
      href={validatedUrl}
      target='_blank'
      rel='noreferrer'
      className='contents'
    >
      <div className='flex rounded-lg border border-standard h-[42px] space-x-4 items-center w-full md:max-w-[320px] mr-3 mt-3'>
        <div className='flex items-center justify-center h-10 rounded-tl-lg rounded-bl-lg bg-neutral-950 !text-neutral-200 aspect-square'>
          {(() => {
            switch (type) {
              case 'twitter':
                return <TwitterLogo className='w-6 h-6 shrink-0' />
              case 'facebook':
                return <FacebookLogo className='w-6 h-6 shrink-0' />
              case 'instagram':
                return <InstagramLogo className='w-6 h-6 shrink-0' />
              case 'tiktok':
                return <TikTokLogo className='w-6 h-6 shrink-0' />
              case 'website':
                return <GlobeAltIcon className='w-6 h-6 shrink-0' />
              default:
                return null
            }
          })()}
        </div>
        <div>
          <span className='text-foreground dark:text-dark-foreground'>
            {socialCardLabels[type]}
          </span>
        </div>
      </div>
    </Link>
  )
}
